import React, { useState, Fragment } from 'react';
import { ArrowUpIcon, ArrowDownIcon, AlertCircleIcon, CheckIcon, ClockIcon, DollarSignIcon, BarChart2Icon, ChevronRightIcon, TrendingUpIcon, TrendingDownIcon, MailIcon, PhoneIcon, CalendarIcon, BellIcon, ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { StatusBadge } from '../../StatusBadge';
export function InvoiceAgingReport({
  filters,
  locale
}) {
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'chart', 'trends'
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('all');
  const [showPredictiveInsights, setShowPredictiveInsights] = useState(false);
  // Sample data
  const invoices = [{
    id: 'INV-2023-001',
    invoiceNumber: 'V00123456',
    vendor: 'Server Solutions Inc.',
    status: 'Pending Approval',
    amount: 12500.0,
    currency: 'USD',
    issueDate: '2023-10-15',
    dueDate: '2023-11-14',
    poNumber: 'PO-2023-001',
    entity: 'Global Technologies Inc.',
    site: 'New York HQ',
    approver: 'John Smith',
    paymentTerms: 'Net 30',
    description: 'Server Hardware',
    vendorRating: 'A',
    paymentHistory: 'Good',
    riskScore: 'Low',
    historicalPaymentDays: 28,
    lineItems: [{
      id: 'ITEM-001',
      description: 'Server Rack',
      quantity: 2,
      unitPrice: 1500,
      total: 3000
    }, {
      id: 'ITEM-002',
      description: 'Server Unit',
      quantity: 3,
      unitPrice: 3000,
      total: 9000
    }, {
      id: 'ITEM-003',
      description: 'Network Switch',
      quantity: 1,
      unitPrice: 500,
      total: 500
    }]
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'PW-5678',
    vendor: 'PrintWorks Inc.',
    status: 'Approved',
    amount: 3200.5,
    currency: 'USD',
    issueDate: '2023-10-05',
    dueDate: '2023-11-04',
    poNumber: 'PO-2023-002',
    entity: 'Global Technologies Inc.',
    site: 'San Francisco Office',
    approver: 'Jane Doe',
    paymentTerms: 'Net 30',
    description: 'Marketing Materials',
    vendorRating: 'B',
    paymentHistory: 'Average',
    riskScore: 'Medium',
    historicalPaymentDays: 32,
    lineItems: [{
      id: 'ITEM-004',
      description: 'Brochures',
      quantity: 1000,
      unitPrice: 1.5,
      total: 1500
    }, {
      id: 'ITEM-005',
      description: 'Banners',
      quantity: 5,
      unitPrice: 200,
      total: 1000
    }, {
      id: 'ITEM-006',
      description: 'Business Cards',
      quantity: 500,
      unitPrice: 0.75,
      total: 375
    }, {
      id: 'ITEM-007',
      description: 'Stickers',
      quantity: 1000,
      unitPrice: 0.25,
      total: 250
    }]
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'OD-9876',
    vendor: 'Office Depot',
    status: 'Paid',
    amount: 1850.75,
    currency: 'USD',
    issueDate: '2023-09-15',
    dueDate: '2023-10-15',
    paymentDate: '2023-10-10',
    poNumber: 'PO-2023-003',
    entity: 'Global Technologies Inc.',
    site: 'New York HQ',
    approver: 'Michael Johnson',
    paymentTerms: 'Net 30',
    description: 'Office Supplies',
    vendorRating: 'A',
    paymentHistory: 'Excellent',
    riskScore: 'Low',
    historicalPaymentDays: 25,
    lineItems: [{
      id: 'ITEM-008',
      description: 'Paper',
      quantity: 50,
      unitPrice: 10,
      total: 500
    }, {
      id: 'ITEM-009',
      description: 'Pens',
      quantity: 100,
      unitPrice: 2,
      total: 200
    }, {
      id: 'ITEM-010',
      description: 'Notebooks',
      quantity: 50,
      unitPrice: 5,
      total: 250
    }, {
      id: 'ITEM-011',
      description: 'Desk Organizers',
      quantity: 25,
      unitPrice: 15,
      total: 375
    }, {
      id: 'ITEM-012',
      description: 'Staplers',
      quantity: 15,
      unitPrice: 8,
      total: 120
    }]
  }, {
    id: 'INV-2023-004',
    invoiceNumber: 'DT-1234',
    vendor: 'Dell Technologies',
    status: 'Paid',
    amount: 8750.0,
    currency: 'USD',
    issueDate: '2023-09-05',
    dueDate: '2023-10-05',
    paymentDate: '2023-10-03',
    poNumber: 'PO-2023-004',
    entity: 'European Operations Ltd',
    site: 'London Office',
    approver: 'Sarah Williams',
    paymentTerms: 'Net 30',
    description: 'IT Equipment',
    vendorRating: 'A',
    paymentHistory: 'Good',
    riskScore: 'Low',
    historicalPaymentDays: 29,
    lineItems: [{
      id: 'ITEM-013',
      description: 'Laptops',
      quantity: 5,
      unitPrice: 1200,
      total: 6000
    }, {
      id: 'ITEM-014',
      description: 'Monitors',
      quantity: 5,
      unitPrice: 350,
      total: 1750
    }, {
      id: 'ITEM-015',
      description: 'Docking Stations',
      quantity: 5,
      unitPrice: 150,
      total: 750
    }, {
      id: 'ITEM-016',
      description: 'Wireless Keyboards',
      quantity: 5,
      unitPrice: 50,
      total: 250
    }]
  }, {
    id: 'INV-2023-005',
    invoiceNumber: 'MS-9876',
    vendor: 'Microsoft',
    status: 'Overdue',
    amount: 5000.0,
    currency: 'USD',
    issueDate: '2023-09-01',
    dueDate: '2023-10-01',
    poNumber: 'PO-2023-005',
    entity: 'Global Technologies Inc.',
    site: 'All Sites',
    approver: 'John Smith',
    paymentTerms: 'Net 30',
    description: 'Software Licenses',
    vendorRating: 'A',
    paymentHistory: 'Good',
    riskScore: 'Medium',
    historicalPaymentDays: 35,
    lineItems: [{
      id: 'ITEM-017',
      description: 'Office 365 Licenses',
      quantity: 50,
      unitPrice: 60,
      total: 3000
    }, {
      id: 'ITEM-018',
      description: 'Windows Server Licenses',
      quantity: 2,
      unitPrice: 1000,
      total: 2000
    }]
  }, {
    id: 'INV-2023-006',
    invoiceNumber: 'IK-5432',
    vendor: 'IKEA Business',
    status: 'Rejected',
    amount: 4200.0,
    currency: 'EUR',
    issueDate: '2023-08-20',
    dueDate: '2023-09-19',
    poNumber: 'PO-2023-006',
    entity: 'European Operations Ltd',
    site: 'London Office',
    approver: 'Robert Brown',
    paymentTerms: 'Net 30',
    description: 'Office Furniture',
    rejectionReason: 'PO was cancelled, invoice received in error',
    vendorRating: 'C',
    paymentHistory: 'Poor',
    riskScore: 'High',
    historicalPaymentDays: 45,
    lineItems: [{
      id: 'ITEM-019',
      description: 'Desks',
      quantity: 5,
      unitPrice: 300,
      total: 1500
    }, {
      id: 'ITEM-020',
      description: 'Chairs',
      quantity: 10,
      unitPrice: 150,
      total: 1500
    }, {
      id: 'ITEM-021',
      description: 'Filing Cabinets',
      quantity: 3,
      unitPrice: 200,
      total: 600
    }, {
      id: 'ITEM-022',
      description: 'Bookshelves',
      quantity: 2,
      unitPrice: 150,
      total: 300
    }, {
      id: 'ITEM-023',
      description: 'Conference Table',
      quantity: 1,
      unitPrice: 300,
      total: 300
    }]
  }, {
    id: 'INV-2023-007',
    invoiceNumber: 'AT-7890',
    vendor: 'AT&T',
    status: 'Overdue',
    amount: 1200.0,
    currency: 'USD',
    issueDate: '2023-08-15',
    dueDate: '2023-09-14',
    poNumber: null,
    entity: 'Global Technologies Inc.',
    site: 'New York HQ',
    approver: 'Michael Johnson',
    paymentTerms: 'Net 30',
    description: 'Telecommunications',
    vendorRating: 'B',
    paymentHistory: 'Average',
    riskScore: 'High',
    historicalPaymentDays: 38,
    lineItems: [{
      id: 'ITEM-024',
      description: 'Phone Services',
      quantity: 1,
      unitPrice: 800,
      total: 800
    }, {
      id: 'ITEM-025',
      description: 'Internet Services',
      quantity: 1,
      unitPrice: 400,
      total: 400
    }]
  }, {
    id: 'INV-2023-008',
    invoiceNumber: 'UPS-4321',
    vendor: 'UPS',
    status: 'Exception',
    amount: 350.0,
    currency: 'USD',
    issueDate: '2023-10-18',
    dueDate: '2023-11-17',
    poNumber: null,
    entity: 'Global Technologies Inc.',
    site: 'San Francisco Office',
    approver: null,
    paymentTerms: 'Net 30',
    description: 'Shipping Services',
    exceptionReason: 'Missing approval',
    vendorRating: 'D',
    paymentHistory: 'New Vendor',
    riskScore: 'Critical',
    historicalPaymentDays: 0,
    lineItems: [{
      id: 'ITEM-026',
      description: 'Shipping Services',
      quantity: 1,
      unitPrice: 350,
      total: 350
    }]
  }];
  // Historical trend data (mock data)
  const trendData = {
    currentMonth: {
      current: 12500,
      '1-30': 8500,
      '31-60': 5000,
      '61-90': 2500,
      '90+': 1500
    },
    previousMonth: {
      current: 14000,
      '1-30': 7500,
      '31-60': 4200,
      '61-90': 3000,
      '90+': 2000
    },
    twoMonthsAgo: {
      current: 13000,
      '1-30': 7000,
      '31-60': 4800,
      '61-90': 2800,
      '90+': 1800
    }
  };
  // Calculate days overdue or remaining
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const invoicesWithAging = invoices.map(invoice => {
    const dueDate = new Date(invoice.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    const daysOverdue = Math.round((today - dueDate) / (1000 * 60 * 60 * 24));
    let agingBucket = '';
    if (invoice.status === 'Paid') {
      agingBucket = 'Paid';
    } else if (daysOverdue <= 0) {
      agingBucket = 'Current';
    } else if (daysOverdue <= 30) {
      agingBucket = '1-30 Days';
    } else if (daysOverdue <= 60) {
      agingBucket = '31-60 Days';
    } else if (daysOverdue <= 90) {
      agingBucket = '61-90 Days';
    } else {
      agingBucket = 'Over 90 Days';
    }
    // Calculate predicted payment date based on vendor history
    const predictedPaymentDays = invoice.historicalPaymentDays || 30;
    const issueDate = new Date(invoice.issueDate);
    const predictedPaymentDate = new Date(issueDate);
    predictedPaymentDate.setDate(predictedPaymentDate.getDate() + predictedPaymentDays);
    // Calculate cash flow impact (simple model)
    const cashFlowImpact = invoice.status !== 'Paid' ? invoice.amount : 0;
    return {
      ...invoice,
      daysOverdue,
      agingBucket,
      predictedPaymentDate,
      cashFlowImpact
    };
  });
  // Filter invoices based on the provided filters
  const filteredInvoices = invoicesWithAging.filter(invoice => {
    const invoiceDate = new Date(invoice.issueDate);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    const dateInRange = (!startDate || invoiceDate >= startDate) && (!endDate || invoiceDate <= endDate);
    const matchesEntity = !filters.entity || invoice.entity.includes(filters.entity);
    const matchesSite = !filters.site || invoice.site.includes(filters.site);
    const matchesVendor = !filters.vendor || invoice.vendor.includes(filters.vendor);
    const matchesCurrency = !filters.currency || invoice.currency === filters.currency;
    const matchesStatus = !filters.status || invoice.status.toLowerCase().includes(filters.status.toLowerCase());
    const matchesRiskLevel = selectedRiskLevel === 'all' || invoice.riskScore === selectedRiskLevel;
    return dateInRange && matchesEntity && matchesSite && matchesVendor && matchesCurrency && matchesStatus && matchesRiskLevel;
  });
  // Sort invoices
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'id':
        comparison = a.id.localeCompare(b.id);
        break;
      case 'invoiceNumber':
        comparison = a.invoiceNumber.localeCompare(b.invoiceNumber);
        break;
      case 'vendor':
        comparison = a.vendor.localeCompare(b.vendor);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'issueDate':
        comparison = new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime();
        break;
      case 'dueDate':
        comparison = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        break;
      case 'amount':
        comparison = a.amount - b.amount;
        break;
      case 'daysOverdue':
        comparison = a.daysOverdue - b.daysOverdue;
        break;
      case 'riskScore':
        const riskOrder = {
          Low: 1,
          Medium: 2,
          High: 3,
          Critical: 4
        };
        comparison = riskOrder[a.riskScore] - riskOrder[b.riskScore];
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Calculate summary statistics
  const totalInvoices = filteredInvoices.length;
  const totalAmount = filteredInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const statusCounts = filteredInvoices.reduce((counts, invoice) => {
    counts[invoice.status] = (counts[invoice.status] || 0) + 1;
    return counts;
  }, {});
  const agingBuckets = filteredInvoices.reduce((buckets, invoice) => {
    buckets[invoice.agingBucket] = (buckets[invoice.agingBucket] || 0) + invoice.amount;
    return buckets;
  }, {});
  // Calculate risk metrics
  const riskCounts = filteredInvoices.reduce((counts, invoice) => {
    counts[invoice.riskScore] = (counts[invoice.riskScore] || 0) + 1;
    return counts;
  }, {});
  const riskAmounts = filteredInvoices.reduce((amounts, invoice) => {
    amounts[invoice.riskScore] = (amounts[invoice.riskScore] || 0) + invoice.amount;
    return amounts;
  }, {});
  // Calculate total cash flow impact
  const totalCashFlowImpact = filteredInvoices.filter(inv => inv.status !== 'Paid').reduce((sum, inv) => sum + inv.amount, 0);
  // Predictive cash flow timeline (next 30 days)
  const cashFlowTimeline = Array(30).fill(0).map((_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    const expectedPayments = filteredInvoices.filter(inv => inv.status !== 'Paid' && inv.predictedPaymentDate <= date).reduce((sum, inv) => sum + inv.amount, 0);
    return {
      day: i + 1,
      date: date.toISOString().split('T')[0],
      amount: expectedPayments
    };
  });
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const toggleInvoiceExpansion = invoiceId => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
    } else {
      setExpandedInvoice(invoiceId);
    }
  };
  const getStatusBadge = status => {
    switch (status) {
      case 'Paid':
        return <StatusBadge status="success" text={status} data-id="element-15408" />;
      case 'Approved':
        return <StatusBadge status="info" text={status} data-id="element-15409" />;
      case 'Pending Approval':
        return <StatusBadge status="warning" text={status} data-id="element-15410" />;
      case 'Overdue':
        return <StatusBadge status="error" text={status} data-id="element-15411" />;
      case 'Rejected':
        return <StatusBadge status="error" text={status} data-id="element-15412" />;
      case 'Exception':
        return <StatusBadge status="error" text={status} data-id="element-15413" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-15414" />;
    }
  };
  const getAgingBadge = (agingBucket, daysOverdue) => {
    switch (agingBucket) {
      case 'Paid':
        return <StatusBadge status="success" text="Paid" data-id="element-15415" />;
      case 'Current':
        return <StatusBadge status="success" text={`Due in ${-daysOverdue} days`} data-id="element-15416" />;
      case '1-30 Days':
        return <StatusBadge status="warning" text={`${daysOverdue} days overdue`} data-id="element-15417" />;
      case '31-60 Days':
        return <StatusBadge status="error" text={`${daysOverdue} days overdue`} data-id="element-15418" />;
      case '61-90 Days':
        return <StatusBadge status="error" text={`${daysOverdue} days overdue`} data-id="element-15419" />;
      case 'Over 90 Days':
        return <StatusBadge status="error" text={`${daysOverdue} days overdue`} data-id="element-15420" />;
      default:
        return <StatusBadge status="default" text={agingBucket} data-id="element-15421" />;
    }
  };
  const getRiskBadge = riskScore => {
    switch (riskScore) {
      case 'Low':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-15422">
            Low
          </span>;
      case 'Medium':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-15423">
            Medium
          </span>;
      case 'High':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800" data-id="element-15424">
            High
          </span>;
      case 'Critical':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-15425">
            Critical
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-15426">
            Unknown
          </span>;
    }
  };
  // Function to calculate month-over-month change
  const calculateMoMChange = (current, previous) => {
    if (!previous) return 0;
    return (current - previous) / previous * 100;
  };
  // Function to render trend indicators
  const renderTrendIndicator = change => {
    if (change > 0) {
      return <span className="text-red-500 flex items-center" data-id="element-15427">
          <TrendingUpIcon className="h-4 w-4 mr-1" data-id="element-15428" />
          {change.toFixed(1)}%
        </span>;
    } else if (change < 0) {
      return <span className="text-green-500 flex items-center" data-id="element-15429">
          <TrendingDownIcon className="h-4 w-4 mr-1" data-id="element-15430" />
          {Math.abs(change).toFixed(1)}%
        </span>;
    }
    return <span className="text-gray-500" data-id="element-15431">0%</span>;
  };
  return <div className="p-6" data-id="element-15432">
      <div className="mb-8" data-id="element-15433">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-15434">
          Invoice Aging Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15435">
          Analysis of invoices by due date and aging buckets
        </p>
      </div>
      {/* Risk filter */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-15436">
        <div className="flex items-center" data-id="element-15437">
          <span className="text-sm font-medium text-gray-700 mr-3" data-id="element-15438">
            Risk Level:
          </span>
          <div className="flex space-x-2" data-id="element-15439">
            <button onClick={() => setSelectedRiskLevel('all')} className={`px-2 py-1 text-xs font-medium rounded ${selectedRiskLevel === 'all' ? 'bg-gray-200' : 'bg-white border border-gray-300'}`} data-id="element-15440">
              All
            </button>
            <button onClick={() => setSelectedRiskLevel('Low')} className={`px-2 py-1 text-xs font-medium rounded ${selectedRiskLevel === 'Low' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300'}`} data-id="element-15441">
              Low
            </button>
            <button onClick={() => setSelectedRiskLevel('Medium')} className={`px-2 py-1 text-xs font-medium rounded ${selectedRiskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-white border border-gray-300'}`} data-id="element-15442">
              Medium
            </button>
            <button onClick={() => setSelectedRiskLevel('High')} className={`px-2 py-1 text-xs font-medium rounded ${selectedRiskLevel === 'High' ? 'bg-orange-100 text-orange-800' : 'bg-white border border-gray-300'}`} data-id="element-15443">
              High
            </button>
            <button onClick={() => setSelectedRiskLevel('Critical')} className={`px-2 py-1 text-xs font-medium rounded ${selectedRiskLevel === 'Critical' ? 'bg-red-100 text-red-800' : 'bg-white border border-gray-300'}`} data-id="element-15444">
              Critical
            </button>
          </div>
          <div className="ml-auto" data-id="element-15445">
            <button onClick={() => setShowPredictiveInsights(!showPredictiveInsights)} className={`flex items-center px-3 py-1 text-sm font-medium rounded-md ${showPredictiveInsights ? 'bg-indigo-100 text-indigo-700' : 'bg-white border border-gray-300'}`} data-id="element-15446">
              <CalendarIcon className="h-4 w-4 mr-1" data-id="element-15447" />
              {showPredictiveInsights ? 'Hide Predictions' : 'Show Predictions'}
            </button>
          </div>
        </div>
      </div>
      {/* Summary cards */}
      <div className="p-4 sm:p-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-15448">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15449">
          <div className="p-5" data-id="element-15450">
            <div className="flex items-center" data-id="element-15451">
              <div className="flex-shrink-0" data-id="element-15452">
                <BarChart2Icon className="h-6 w-6 text-gray-400" data-id="element-15453" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15454">
                <dl data-id="element-15455">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15456">
                    Total Invoices
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-15457">
                    {totalInvoices}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15458">
          <div className="p-5" data-id="element-15459">
            <div className="flex items-center" data-id="element-15460">
              <div className="flex-shrink-0" data-id="element-15461">
                <CheckIcon className="h-6 w-6 text-green-400" data-id="element-15462" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15463">
                <dl data-id="element-15464">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15465">
                    Paid Invoices
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-15466">
                    {statusCounts['Paid'] || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15467">
          <div className="p-5" data-id="element-15468">
            <div className="flex items-center" data-id="element-15469">
              <div className="flex-shrink-0" data-id="element-15470">
                <AlertCircleIcon className="h-6 w-6 text-red-400" data-id="element-15471" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15472">
                <dl data-id="element-15473">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15474">
                    Overdue Invoices
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-15475">
                    {statusCounts['Overdue'] || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15476">
          <div className="p-5" data-id="element-15477">
            <div className="flex items-center" data-id="element-15478">
              <div className="flex-shrink-0" data-id="element-15479">
                <DollarSignIcon className="h-6 w-6 text-indigo-400" data-id="element-15480" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15481">
                <dl data-id="element-15482">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15483">
                    Total Amount
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-15484">
                    {formatCurrency(totalAmount, 'USD', locale)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Cash flow impact card */}
      <div className="px-4 sm:px-6 mb-6" data-id="element-15485">
        <div className="bg-indigo-50 rounded-lg p-4" data-id="element-15486">
          <h4 className="text-base font-medium text-indigo-800 mb-2" data-id="element-15487">
            Cash Flow Impact
          </h4>
          <div className="text-2xl font-bold text-indigo-900" data-id="element-15488">
            {formatCurrency(totalCashFlowImpact, 'USD', locale)}
          </div>
          <p className="text-sm text-indigo-700 mt-1" data-id="element-15489">
            Total outstanding amount affecting your cash flow
          </p>
        </div>
      </div>
      {viewMode === 'chart' && <>
          {/* Aging buckets visualization */}
          <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-15490">
            <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15491">
              Aging Distribution
            </h4>
            <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-15492">
              <div className="flex h-10 mb-2 w-full" data-id="element-15493">
                {Object.entries(agingBuckets).map(([bucket, amount]) => {
              // Determine color based on bucket
              let bgColor = 'bg-gray-300';
              if (bucket === 'Paid') bgColor = 'bg-green-500';else if (bucket === 'Current') bgColor = 'bg-blue-500';else if (bucket === '1-30 Days') bgColor = 'bg-yellow-500';else if (bucket === '31-60 Days') bgColor = 'bg-orange-500';else if (bucket === '61-90 Days') bgColor = 'bg-red-500';else if (bucket === 'Over 90 Days') bgColor = 'bg-red-700';
              // Calculate width as percentage
              const percentage = Math.round(amount / totalAmount * 100);
              return <div key={bucket} className={`${bgColor} relative`} style={{
                width: `${percentage}%`
              }} title={`${bucket}: ${formatCurrency(amount, 'USD', locale)} (${percentage}%)`} data-id="element-15494">
                      {percentage >= 10 && <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white" data-id="element-15495">
                          {percentage}%
                        </span>}
                    </div>;
            })}
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4" data-id="element-15496">
                {Object.entries(agingBuckets).map(([bucket, amount]) => <div key={bucket} className="text-center" data-id="element-15497">
                    <div className="text-xs font-medium text-gray-500" data-id="element-15498">
                      {bucket}
                    </div>
                    <div className="font-semibold text-gray-900" data-id="element-15499">
                      {formatCurrency(amount, 'USD', locale)}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-15500">
                      {Math.round(amount / totalAmount * 100)}%
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Risk visualization */}
          <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-15501">
            <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15502">
              Risk Analysis
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-15503">
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-15504">
                <h5 className="text-sm font-medium text-gray-700 mb-3" data-id="element-15505">
                  Risk Distribution by Amount
                </h5>
                <div className="flex h-10 mb-2 w-full" data-id="element-15506">
                  {Object.entries(riskAmounts).map(([risk, amount]) => {
                // Skip if amount is 0
                if (amount === 0) return null;
                // Determine color based on risk
                let bgColor = 'bg-gray-300';
                if (risk === 'Low') bgColor = 'bg-green-500';else if (risk === 'Medium') bgColor = 'bg-yellow-500';else if (risk === 'High') bgColor = 'bg-orange-500';else if (risk === 'Critical') bgColor = 'bg-red-500';
                // Calculate width as percentage
                const percentage = Math.round(amount / totalAmount * 100);
                return <div key={risk} className={`${bgColor} relative`} style={{
                  width: `${percentage}%`
                }} title={`${risk}: ${formatCurrency(amount, 'USD', locale)} (${percentage}%)`} data-id="element-15507">
                        {percentage >= 10 && <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white" data-id="element-15508">
                            {percentage}%
                          </span>}
                      </div>;
              })}
                </div>
                <div className="grid grid-cols-4 gap-2 mt-4" data-id="element-15509">
                  {Object.entries(riskAmounts).map(([risk, amount]) => <div key={risk} className="text-center" data-id="element-15510">
                      <div className="text-xs font-medium text-gray-500" data-id="element-15511">
                        {risk}
                      </div>
                      <div className="font-semibold text-gray-900" data-id="element-15512">
                        {formatCurrency(amount, 'USD', locale)}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-15513">
                        {Math.round(amount / totalAmount * 100)}%
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-15514">
                <h5 className="text-sm font-medium text-gray-700 mb-3" data-id="element-15515">
                  Risk Distribution by Invoice Count
                </h5>
                <div className="flex h-10 mb-2 w-full" data-id="element-15516">
                  {Object.entries(riskCounts).map(([risk, count]) => {
                // Skip if count is 0
                if (count === 0) return null;
                // Determine color based on risk
                let bgColor = 'bg-gray-300';
                if (risk === 'Low') bgColor = 'bg-green-500';else if (risk === 'Medium') bgColor = 'bg-yellow-500';else if (risk === 'High') bgColor = 'bg-orange-500';else if (risk === 'Critical') bgColor = 'bg-red-500';
                // Calculate width as percentage
                const percentage = Math.round(count / totalInvoices * 100);
                return <div key={risk} className={`${bgColor} relative`} style={{
                  width: `${percentage}%`
                }} title={`${risk}: ${count} invoices (${percentage}%)`} data-id="element-15517">
                        {percentage >= 10 && <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white" data-id="element-15518">
                            {percentage}%
                          </span>}
                      </div>;
              })}
                </div>
                <div className="grid grid-cols-4 gap-2 mt-4" data-id="element-15519">
                  {Object.entries(riskCounts).map(([risk, count]) => <div key={risk} className="text-center" data-id="element-15520">
                      <div className="text-xs font-medium text-gray-500" data-id="element-15521">
                        {risk}
                      </div>
                      <div className="font-semibold text-gray-900" data-id="element-15522">
                        {count} {count === 1 ? 'invoice' : 'invoices'}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-15523">
                        {Math.round(count / totalInvoices * 100)}%
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </>}
      {viewMode === 'trends' && <>
          {/* Trend analysis */}
          <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-15524">
            <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15525">
              Aging Trend Analysis
            </h4>
            <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-15526">
              <div className="overflow-x-auto" data-id="element-15527">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-15528">
                  <thead className="bg-gray-50" data-id="element-15529">
                    <tr data-id="element-15530">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15531">
                        Aging Bucket
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15532">
                        Current Month
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15533">
                        MoM Change
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15534">
                        Previous Month
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15535">
                        Two Months Ago
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15536">
                        3-Month Trend
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-15537">
                    {Object.entries(trendData.currentMonth).map(([bucket, amount]) => {
                  const previousAmount = trendData.previousMonth[bucket];
                  const twoMonthsAgoAmount = trendData.twoMonthsAgo[bucket];
                  const momChange = calculateMoMChange(amount, previousAmount);
                  return <tr key={bucket} data-id="element-15538">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15539">
                              {bucket === 'current' ? 'Current' : bucket}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900" data-id="element-15540">
                              {formatCurrency(amount, 'USD', locale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-15541">
                              {renderTrendIndicator(momChange)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500" data-id="element-15542">
                              {formatCurrency(previousAmount, 'USD', locale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500" data-id="element-15543">
                              {formatCurrency(twoMonthsAgoAmount, 'USD', locale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-15544">
                              <div className="flex justify-end items-center" data-id="element-15545">
                                {/* Simple trend visualization */}
                                <div className="flex items-center space-x-1" data-id="element-15546">
                                  <div className={`h-4 w-4 rounded-sm ${twoMonthsAgoAmount > previousAmount ? 'bg-red-200' : 'bg-green-200'}`} title={formatCurrency(twoMonthsAgoAmount, 'USD', locale)} data-id="element-15547"></div>
                                  <div className={`h-4 w-4 rounded-sm ${previousAmount > amount ? 'bg-green-400' : 'bg-red-400'}`} title={formatCurrency(previousAmount, 'USD', locale)} data-id="element-15548"></div>
                                  <div className={`h-4 w-4 rounded-sm ${amount > previousAmount ? 'bg-red-600' : 'bg-green-600'}`} title={formatCurrency(amount, 'USD', locale)} data-id="element-15549"></div>
                                </div>
                              </div>
                            </td>
                          </tr>;
                })}
                    <tr className="bg-gray-50" data-id="element-15550">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15551">
                        Total
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-900" data-id="element-15552">
                        {formatCurrency(Object.values(trendData.currentMonth).reduce((a, b) => a + b, 0), 'USD', locale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-15553">
                        {renderTrendIndicator(calculateMoMChange(Object.values(trendData.currentMonth).reduce((a, b) => a + b, 0), Object.values(trendData.previousMonth).reduce((a, b) => a + b, 0)))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-900" data-id="element-15554">
                        {formatCurrency(Object.values(trendData.previousMonth).reduce((a, b) => a + b, 0), 'USD', locale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-900" data-id="element-15555">
                        {formatCurrency(Object.values(trendData.twoMonthsAgo).reduce((a, b) => a + b, 0), 'USD', locale)}
                      </td>
                      <td data-id="element-15556"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Predictive cash flow timeline */}
          {showPredictiveInsights && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-15557">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15558">
                Predictive Cash Flow Timeline (Next 30 Days)
              </h4>
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-15559">
                <div className="flex flex-col space-y-2" data-id="element-15560">
                  <div className="flex items-center" data-id="element-15561">
                    <div className="w-24 text-xs font-medium text-gray-500" data-id="element-15562">
                      Today
                    </div>
                    <div className="flex-1 h-6 bg-gray-100 rounded" data-id="element-15563">
                      <div className="h-6 bg-indigo-500 rounded" style={{
                  width: '0%'
                }} data-id="element-15564"></div>
                    </div>
                    <div className="w-32 text-right text-xs font-medium text-gray-900" data-id="element-15565">
                      {formatCurrency(0, 'USD', locale)}
                    </div>
                  </div>
                  {cashFlowTimeline.filter((_, i) => i % 5 === 4 || i === 29).map(day => {
              const percentage = Math.min(100, day.amount / totalCashFlowImpact * 100);
              return <div key={day.day} className="flex items-center" data-id="element-15566">
                          <div className="w-24 text-xs font-medium text-gray-500" data-id="element-15567">
                            Day {day.day}
                          </div>
                          <div className="flex-1 h-6 bg-gray-100 rounded" data-id="element-15568">
                            <div className="h-6 bg-indigo-500 rounded" style={{
                    width: `${percentage}%`
                  }} data-id="element-15569"></div>
                          </div>
                          <div className="w-32 text-right text-xs font-medium text-gray-900" data-id="element-15570">
                            {formatCurrency(day.amount, 'USD', locale)}
                          </div>
                        </div>;
            })}
                </div>
                <div className="mt-4 text-sm text-gray-500" data-id="element-15571">
                  <p data-id="element-15572">
                    This timeline shows predicted cumulative payment collections
                    based on vendor payment history.
                  </p>
                </div>
              </div>
            </div>}
        </>}
      {/* Aging buckets summary - show in all views */}
      <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-15573">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15574">
          Aging Summary
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-15575">
          {Object.entries(agingBuckets).map(([bucket, amount]) => <div key={bucket} className="bg-white overflow-hidden shadow-sm rounded-lg p-4" data-id="element-15576">
              <div className="text-sm font-medium text-gray-500" data-id="element-15577">{bucket}</div>
              <div className="mt-1 text-xl font-semibold text-gray-900" data-id="element-15578">
                {formatCurrency(amount, 'USD', locale)}
              </div>
              <div className="mt-1 text-xs text-gray-500" data-id="element-15579">
                {Math.round(amount / totalAmount * 100)}% of total
              </div>
            </div>)}
        </div>
      </div>
      {/* Invoices Table - only show in table view */}
      {viewMode === 'table' && <div className="px-4 py-5 sm:p-6" data-id="element-15580">
          <div className="flex flex-col" data-id="element-15581">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-15582">
              <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-15583">
                <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-15584">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-15585">
                    <thead className="bg-gray-50" data-id="element-15586">
                      <tr data-id="element-15587">
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15588">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('invoiceNumber')} data-id="element-15589">
                            <span data-id="element-15590">Invoice #</span>
                            {sortField === 'invoiceNumber' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15591" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15592" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15593" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15594">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('vendor')} data-id="element-15595">
                            <span data-id="element-15596">Vendor</span>
                            {sortField === 'vendor' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15597" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15598" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15599" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15600">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('status')} data-id="element-15601">
                            <span data-id="element-15602">Status</span>
                            {sortField === 'status' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15603" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15604" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15605" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15606">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('issueDate')} data-id="element-15607">
                            <span data-id="element-15608">Issue Date</span>
                            {sortField === 'issueDate' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15609" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15610" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15611" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15612">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('dueDate')} data-id="element-15613">
                            <span data-id="element-15614">Due Date</span>
                            {sortField === 'dueDate' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15615" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15616" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15617" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15618">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('amount')} data-id="element-15619">
                            <span data-id="element-15620">Amount</span>
                            {sortField === 'amount' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15621" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15622" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15623" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15624">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('daysOverdue')} data-id="element-15625">
                            <span data-id="element-15626">Aging</span>
                            {sortField === 'daysOverdue' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15627" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15628" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15629" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15630">
                          <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('riskScore')} data-id="element-15631">
                            <span data-id="element-15632">Risk</span>
                            {sortField === 'riskScore' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-15633" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-15634" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-15635" />}
                          </button>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15636">
                          <span data-id="element-15637">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-15638">
                      {sortedInvoices.map(invoice => <Fragment key={invoice.id} data-id="element-15639">
                          <tr className={expandedInvoice === invoice.id ? 'bg-indigo-50' : 'hover:bg-gray-50'} data-id="element-15640">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-15641">
                              {invoice.invoiceNumber}
                              <div className="text-xs text-gray-500" data-id="element-15642">
                                {invoice.id}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-15643">
                              {invoice.vendor}
                              <div className="text-xs text-gray-500" data-id="element-15644">
                                {invoice.description}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-15645">
                              {getStatusBadge(invoice.status)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15646">
                              {new Date(invoice.issueDate).toLocaleDateString(locale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15647">
                              {new Date(invoice.dueDate).toLocaleDateString(locale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-15648">
                              {formatCurrency(invoice.amount, invoice.currency, locale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-15649">
                              {getAgingBadge(invoice.agingBucket, invoice.daysOverdue)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-15650">
                              {getRiskBadge(invoice.riskScore)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-15651">
                              <div className="flex space-x-2" data-id="element-15652">
                                <button onClick={() => toggleInvoiceExpansion(invoice.id)} className="text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-15653">
                                  <span data-id="element-15654">Details</span>
                                  <ChevronRightIcon className={`ml-1 h-5 w-5 transform transition-transform ${expandedInvoice === invoice.id ? 'rotate-90' : ''}`} data-id="element-15655" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedInvoice === invoice.id && <tr data-id="element-15656">
                              <td colSpan={9} className="px-6 py-4 bg-gray-50" data-id="element-15657">
                                <div className="border rounded-md p-4 bg-white" data-id="element-15658">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-15659">
                                    <div data-id="element-15660">
                                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-15661">
                                        Entity / Site
                                      </h4>
                                      <p className="text-sm text-gray-900" data-id="element-15662">
                                        {invoice.entity} - {invoice.site}
                                      </p>
                                    </div>
                                    <div data-id="element-15663">
                                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-15664">
                                        PO Number
                                      </h4>
                                      <p className="text-sm text-gray-900" data-id="element-15665">
                                        {invoice.poNumber || 'N/A'}
                                      </p>
                                    </div>
                                    <div data-id="element-15666">
                                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-15667">
                                        Payment Terms
                                      </h4>
                                      <p className="text-sm text-gray-900" data-id="element-15668">
                                        {invoice.paymentTerms}
                                      </p>
                                    </div>
                                  </div>
                                  {/* Predictive insights section */}
                                  {showPredictiveInsights && invoice.status !== 'Paid' && <div className="mb-4 p-3 bg-blue-50 rounded-md" data-id="element-15669">
                                        <h4 className="text-xs font-medium text-blue-800 uppercase tracking-wider mb-1" data-id="element-15670">
                                          Payment Prediction
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-id="element-15671">
                                          <div data-id="element-15672">
                                            <p className="text-sm text-blue-800" data-id="element-15673">
                                              <span className="font-medium" data-id="element-15674">
                                                Predicted Payment:
                                              </span>{' '}
                                              {invoice.predictedPaymentDate.toLocaleDateString(locale)}
                                            </p>
                                            <p className="text-xs text-blue-600" data-id="element-15675">
                                              Based on vendor's historical
                                              payment pattern
                                            </p>
                                          </div>
                                          <div data-id="element-15676">
                                            <p className="text-sm text-blue-800" data-id="element-15677">
                                              <span className="font-medium" data-id="element-15678">
                                                Vendor Rating:
                                              </span>{' '}
                                              {invoice.vendorRating}
                                            </p>
                                            <p className="text-xs text-blue-600" data-id="element-15679">
                                              Payment history:{' '}
                                              {invoice.paymentHistory}
                                            </p>
                                          </div>
                                          <div data-id="element-15680">
                                            <p className="text-sm text-blue-800" data-id="element-15681">
                                              <span className="font-medium" data-id="element-15682">
                                                Risk Assessment:
                                              </span>{' '}
                                              {invoice.riskScore}
                                            </p>
                                            <p className="text-xs text-blue-600" data-id="element-15683">
                                              Based on payment history and
                                              current status
                                            </p>
                                          </div>
                                        </div>
                                      </div>}
                                  {invoice.status === 'Paid' && <div className="mb-4 p-3 bg-green-50 rounded-md" data-id="element-15684">
                                      <h4 className="text-xs font-medium text-green-800 uppercase tracking-wider mb-1" data-id="element-15685">
                                        Payment Details
                                      </h4>
                                      <p className="text-sm text-green-800" data-id="element-15686">
                                        Paid on{' '}
                                        {new Date(invoice.paymentDate).toLocaleDateString(locale)}
                                      </p>
                                    </div>}
                                  {invoice.status === 'Rejected' && <div className="mb-4 p-3 bg-red-50 rounded-md" data-id="element-15687">
                                      <h4 className="text-xs font-medium text-red-800 uppercase tracking-wider mb-1" data-id="element-15688">
                                        Rejection Details
                                      </h4>
                                      <p className="text-sm text-red-800" data-id="element-15689">
                                        Reason: {invoice.rejectionReason}
                                      </p>
                                    </div>}
                                  {invoice.status === 'Exception' && <div className="mb-4 p-3 bg-red-50 rounded-md" data-id="element-15690">
                                      <h4 className="text-xs font-medium text-red-800 uppercase tracking-wider mb-1" data-id="element-15691">
                                        Exception Details
                                      </h4>
                                      <p className="text-sm text-red-800" data-id="element-15692">
                                        Reason: {invoice.exceptionReason}
                                      </p>
                                    </div>}
                                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-15693">
                                    Line Items
                                  </h4>
                                  <div className="overflow-x-auto" data-id="element-15694">
                                    <table className="min-w-full divide-y divide-gray-200" data-id="element-15695">
                                      <thead className="bg-gray-50" data-id="element-15696">
                                        <tr data-id="element-15697">
                                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15698">
                                            Item ID
                                          </th>
                                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15699">
                                            Description
                                          </th>
                                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15700">
                                            Quantity
                                          </th>
                                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15701">
                                            Unit Price
                                          </th>
                                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15702">
                                            Total
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-15703">
                                        {invoice.lineItems.map(item => <tr key={item.id} data-id="element-15704">
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-15705">
                                              {item.id}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900" data-id="element-15706">
                                              {item.description}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-15707">
                                              {item.quantity}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-15708">
                                              {formatCurrency(item.unitPrice, invoice.currency, locale)}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900" data-id="element-15709">
                                              {formatCurrency(item.total, invoice.currency, locale)}
                                            </td>
                                          </tr>)}
                                      </tbody>
                                      <tfoot data-id="element-15710">
                                        <tr className="bg-gray-50" data-id="element-15711">
                                          <td colSpan={4} className="px-3 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-15712">
                                            Total:
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900" data-id="element-15713">
                                            {formatCurrency(invoice.amount, invoice.currency, locale)}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                  {/* Action buttons */}
                                  {invoice.status !== 'Paid' && <div className="mt-4 flex justify-end space-x-2" data-id="element-15714">
                                      <button className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-15715">
                                        <MailIcon className="h-3 w-3 mr-1" data-id="element-15716" />
                                        Email Reminder
                                      </button>
                                      <button className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-15717">
                                        <PhoneIcon className="h-3 w-3 mr-1" data-id="element-15718" />
                                        Contact Vendor
                                      </button>
                                      <button className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-15719">
                                        <MessageCircleIcon className="h-3 w-3 mr-1" data-id="element-15720" />
                                        Add Note
                                      </button>
                                      <button className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-15721">
                                        <ArrowRightIcon className="h-3 w-3 mr-1" data-id="element-15722" />
                                        Process Payment
                                      </button>
                                    </div>}
                                </div>
                              </td>
                            </tr>}
                        </Fragment>)}
                    </tbody>
                  </table>
                  {sortedInvoices.length === 0 && <div className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-15723">
                      No invoices found matching the selected filters.
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}